import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "components/datatable/Datatable";
import type { Column } from "types/Datatable";
import PaginationControls from "components/pagination/PaginationControls";
import UserMergeTabs from "features/users/user-merge/UserMergeTabs";
import type { AppDispatch, RootState } from "configureStore";
import {
  fetchMergeHistoryListThunk,
  resetList,
  setListPage,
  setListPageSize,
} from "./user-merge-history-container.slice";
import {
  formatMergeHistoryTimestamp,
  formatUser,
} from "./user-merge-history-formatters";

export default function UserMergeHistoryContainer() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const previousCompanyId = useRef(companyId);
  const skipNextResetFetch = useRef(false);

  const { rows, pagination, isLoading, isError, page, pageSize } = useSelector(
    (state: RootState) => state.mergeHistoryContainerReducer.list
  );

  useEffect(() => {
    return () => {
      dispatch(resetList());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    if (previousCompanyId.current !== companyId) {
      previousCompanyId.current = companyId;
      dispatch(resetList());
      skipNextResetFetch.current = page !== 1 || pageSize !== 25;
      dispatch(fetchMergeHistoryListThunk(companyId));
      return;
    }

    if (skipNextResetFetch.current) {
      skipNextResetFetch.current = false;
      return;
    }

    dispatch(fetchMergeHistoryListThunk(companyId));
  }, [dispatch, companyId, page, pageSize]);

  const handleRowClick = (item: { mergeBatchId?: string }) => {
    if (item.mergeBatchId && companyId) {
      navigate(`/app/${companyId}/user-merge-history/${item.mergeBatchId}`);
    }
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage > 0 && nextPage !== page) {
      dispatch(setListPage(nextPage));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize > 0 && nextPageSize !== pageSize) {
      dispatch(setListPageSize(nextPageSize));
    }
  };

  const tableData =
    rows.map((r) => ({
      id: r.mergeBatchId,
      mergeBatchId: r.mergeBatchId,
      mergedAtLabel: formatMergeHistoryTimestamp(r.mergedAt),
      sourceLabel: formatUser(r.sourceUser),
      targetLabel: formatUser(r.targetUser),
      actorLabel: formatUser(r.createdByUser),
      stepCount: r.stepCount,
    })) || [];

  const columns = [
    {
      key: "mergedAtLabel",
      name: t("merge_history_merged_at"),
      onClick: handleRowClick,
    },
    {
      key: "sourceLabel",
      name: t("merge_history_source_user"),
      onClick: handleRowClick,
    },
    {
      key: "targetLabel",
      name: t("merge_history_target_user"),
      onClick: handleRowClick,
    },
    {
      key: "actorLabel",
      name: t("merge_history_actor"),
      onClick: handleRowClick,
    },
    {
      key: "stepCount",
      name: t("merge_history_steps"),
      onClick: handleRowClick,
    },
  ] as Column[];

  const totalItems =
    typeof pagination?.total === "number" ? pagination.total : tableData.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
      ? Math.ceil(totalItems / pageSize)
      : 0;
  const hasNextPage =
    typeof pagination?.hasNextPage === "boolean"
      ? pagination.hasNextPage
      : totalPages > 0 && page < totalPages;
  const hasPreviousPage =
    typeof pagination?.hasPreviousPage === "boolean"
      ? pagination.hasPreviousPage
      : page > 1;
  const shouldShowPagination = Boolean(pagination);

  if (!companyId) {
    return null;
  }

  return (
    <div className="space-y-5">
      <UserMergeTabs active="history" />
      <p className="text-sm text-gray-600">{t("user_merge_history_subtitle")}</p>

      {isError && (
        <div className="text-sm text-red-600">
          {t("merge_history_load_error")}
        </div>
      )}

      <div className="relative bg-white rounded-lg border border-gray-200 shadow-sm min-h-[200px]">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
            <span className="text-gray-500 text-sm">{t("feed_loading")}</span>
          </div>
        )}
        {!isLoading && !isError && tableData.length === 0 && (
          <div className="p-8 text-center text-gray-500 text-sm">
            {t("merge_history_empty")}
          </div>
        )}
        {tableData.length > 0 && (
          <DataTable
            collection={tableData}
            columns={columns}
            maxBodyHeight="100%"
          />
        )}
        {shouldShowPagination && (
          <div className="border-t border-[rgba(0,0,0,0.06)] p-4">
            <PaginationControls
              page={page}
              pageSize={pageSize}
              total={totalItems}
              totalPages={totalPages}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
